import { useMemo } from "react";
import { normalizeUserRole, UserRole } from "../enums/UserRole";
import { getLocalStorage } from "../utils/LocalStorage.utils";
import { Upper } from "../utils/String.utils";
export const useUserContext = () => {
    const sessionData = getLocalStorage(`${import.meta.env.VITE_SESSION_PREFIX}-session-data`);
    const meData = getLocalStorage(`${import.meta.env.VITE_SESSION_PREFIX}-me-data`);
    const sessionObject = sessionData;
    if (!sessionObject) {
        return {
            userId: undefined,
            userRole: undefined,
            userName: undefined,
            userGroups: undefined,
            organizationId: undefined,
            originalOrganizationId: undefined,
            isTherapproveAdmin: false,
            isCaseManager: false,
            isMasquerading: false,
            isTokenExpired: true
        };
    }
    const userId = useMemo(() => meData?.id, [meData]);
    const userGroups = useMemo(() => sessionObject["cognito:groups"], [sessionObject]);
    const userRole = useMemo(() => normalizeUserRole(meData?.roleName), [meData]);
    const userName = useMemo(() => {
        if (meData?.name) {
            const [firstName, ...rest] = meData.name.trim().split(/\s+/);
            return {
                firstName,
                lastName: rest.join(" ") || undefined
            };
        }
        return {
            firstName: sessionObject["given_name"],
            lastName: sessionObject["family_name"]
        };
    }, [meData, sessionObject]);
    const organizationId = useMemo(() => {
        if (meData?.masqueradeOrganizationId) {
            return meData.masqueradeOrganizationId;
        }
        if (meData?.organizationId) {
            return meData.organizationId;
        }
        if (userRole === UserRole.ORGANIZATION_OWNER) {
            return (meData?.id ?? null);
        }
        return null;
    }, [meData, userRole]);
    const originalOrganizationId = useMemo(() => {
        if (meData?.organizationId) {
            return meData.organizationId;
        }
        if (userRole === UserRole.ORGANIZATION_OWNER) {
            return (meData?.id ?? null);
        }
        return null;
    }, [meData, userRole]);
    const isTherapproveAdmin = useMemo(() => {
        if (import.meta.env.VITE_ADMIN_GROUP) {
            return (userGroups &&
                userGroups.includes(import.meta.env.VITE_ADMIN_GROUP));
        }
        return false;
    }, [userGroups]);
    const isCaseManager = useMemo(() => {
        return userRole
            ? Upper(userRole) === Upper(UserRole.CASE_MANAGER)
            : false;
    }, [userRole]);
    const isMasquerading = useMemo(() => {
        return !!isTherapproveAdmin && meData?.masqueradeOrganizationId != null;
    }, [isTherapproveAdmin, meData?.masqueradeOrganizationId]);
    const isTokenExpired = useMemo(() => {
        if (sessionObject.exp) {
            return sessionObject.exp < Date.now() / 1000;
        }
        return false;
    }, [sessionObject]);
    return {
        userId,
        userRole,
        userName,
        userGroups,
        organizationId,
        originalOrganizationId,
        isTherapproveAdmin: !!isTherapproveAdmin,
        isCaseManager: !!isCaseManager,
        isMasquerading: !!isMasquerading,
        isTokenExpired: !!isTokenExpired
    };
};
